import {
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
  Autocomplete
} from "@mui/material";
import React, { useEffect, useState, useMemo, useContext, useCallback, useRef } from "react";
import { useTranslation } from "react-i18next";
import { LOOP_RECORD, LOOP_RECORD_EDI_OPTIONS } from "../../../constants/loopRecordOptions";
import TPCheckbox from "../../TemplateProperties/TPCheckbox";
import VariableCollection from "./VariableCollection";
import CustomDBField from "./CustomDBField";
import { ParentScreenCtx } from "/src/pages/workbench_build/WorkbenchBuild";
import { useSelector, useDispatch } from 'react-redux';
import SourceType from "../../TemplateProperties/SourceType";
import SourceTypeInboundDataRenderer from "../../TemplateProperties/SourceTypeComponents/SourceTypeInboundDataRenderer";
import { cloneDeep,debounce } from "lodash";
import { api, useGetSegmentIdsQuery } from "../../../redux/api";
import { inboundTemplateToRcTree } from "../../../utils/rctreeDataConversion";
import { treeActions } from "../../../redux/slices/treeSlice";
import { eachDeep } from "deepdash-es/standalone";
import { filterOutNodeTypes } from "../../../utils/utilFunctions";
import EDIRecordProperties from "./EDIRecordProperties";
import JSONRecordProperties from "./JSONRecordProperties";
import DatabaseRecordProperties from './DatabaseRecordProperties'

export const RecordPropertiesCheckBox = () => {
  const [loopCounterChkBox, setLoopCounterChkBox] = useState(false);
  const [resetLoopChkBox, setResetLoopChkBox] = useState(false);
  const [recordDelimiterEndChkBox, setRecordDelimiterEndChkBox] = useState(false);
  
  const { t } = useTranslation();
  const parentScreen = useContext(ParentScreenCtx)
  const { rightTree } = useSelector(state => state.treeData[parentScreen]);
  const {translatedData,selectedNode} = rightTree.tree[rightTree.currentTab];
  const dispatch = useDispatch()
  const { FILETYPE } = rightTree.tree.templateHeader
  
  useEffect(() => {
    if (selectedNode) {
      const hasAutoDelimit = selectedNode?.hasOwnProperty("AUTO_DELIMIT");
      const autoDelimitValue = selectedNode?.AUTO_DELIMIT;
      const recordDelimiterEndChkBox = hasAutoDelimit
        ? autoDelimitValue === "TRUE"
        : true;

      setRecordDelimiterEndChkBox(recordDelimiterEndChkBox);
      setResetLoopChkBox(selectedNode?.LOOP_RESET === "TRUE" || false);
      setLoopCounterChkBox(selectedNode?.LOOP_COUNT === "TRUE" || false);
    }
  }, [selectedNode?.key]);
  
  const submitChange = (key,value) => {
    const updatedData = eachDeep(cloneDeep(translatedData),(obj) => {
      if(obj?.key !== selectedNode.key) {
        return;
      }
      obj[key] = value === true ? 'TRUE' :'FALSE';
      dispatch(
        treeActions.setRightTreeSelectedNode({
          screen: parentScreen,
          type: rightTree.currentTab,
          data: obj,
        })
      )
    })
     
     dispatch(
      treeActions.setNewRightTreeTranslatedData({
        screen: parentScreen,
        type: rightTree.currentTab,
        data: updatedData
      })
     )
  }
  const debounceChangeHandler = useCallback(debounce(submitChange, 500), [
    rightTree.currentTab,
    rightTree.tree,
  ]);

  useEffect(() => {
    return () => {
      debounceChangeHandler.cancel();
    };
  }, [debounceChangeHandler]);

  return (
    <div className="flex-column">
      {(!['JSON', "XML"].includes(FILETYPE) || (FILETYPE === 'XML' && rightTree.currentTab !== 'body'))  && <FormControl>
        <TPCheckbox
          label={t("BTN_USE_REC_DELIM_AT_END_OF_REC")}
          checked={recordDelimiterEndChkBox}
          handleCheck={(checked) => {
            setRecordDelimiterEndChkBox(checked);
            debounceChangeHandler('AUTO_DELIMIT',checked)
          }}
        />
      </FormControl>}
      <FormControl>
        <TPCheckbox
          label={t("BTN_CREATE_COUNTER_FOR_LOOP")}
          checked={loopCounterChkBox}
          handleCheck={(checked) => {
            setLoopCounterChkBox(checked);
            debounceChangeHandler('LOOP_COUNT',checked)
          }}
        />
      </FormControl>
      <FormControl>
        <TPCheckbox
          label={t("BTN_RESET_LOOP")}
          checked={resetLoopChkBox}
          handleCheck={(checked) => {
            setResetLoopChkBox(checked);
            debounceChangeHandler('LOOP_RESET',checked)
          }}
        />
      </FormControl>
    </div>
  );
};
function OutboundRecordProperties() {
  const { t } = useTranslation();
  const parentScreen = useContext(ParentScreenCtx)
  const { rightTree } = useSelector(state => state.treeData[parentScreen]);
  const DEPENDENCIES = useSelector(state=>state.treeData.outbound.rightTree.tree.templateHeader?.DEPENDENCIES || '');
  const inboundTemplateTree = useSelector(state=>state.treeData.inbound.leftTree.tree.body.translatedData);
  const inboundDependencyTree = useSelector((state) =>state.treeData.outbound.leftTree.inboundDependencyTree);
  const {account} = useSelector(state=>state?.queryParams ||'');
  const {translatedData,selectedNode} = rightTree.tree[rightTree.currentTab];
  const { FILETYPE, FILETYPE_VERSION } = rightTree.tree.templateHeader
  const [description, setDescription] = useState("");
  const [showCheckbox,setShowCheckbox] = useState(true);
  const dispatch = useDispatch()
  const [showCheckboxBefore, setShowCheckboxBefore] = useState(false);
  const [loopRecordOptions, setLoopRecordOptions] = useState([]);
  const [loopRecordValue, setLoopRecordValue] = useState('');
  const [treeData,setTreeData] = useState({header:[],body:[],trailer:[]});
  const [expandActions,setExpandActions] = useState({tab:'',keys:[]});
  const [isLoading, setIsLoading] = useState(false);
  const [tab,setTab] = useState('');
  const [hl7SegmentId,setHL7SegmentId] = useState(t('LBL_LOOP_ONLY'));
  const [segmentOptions, setSegmentOptions] = useState([]);
  const errorRef = useRef(null)
  const hl7SegmentIdOptions = useGetSegmentIdsQuery({
    account,
    report_type: "hl7",
    version: FILETYPE_VERSION
  });

  // Adding the "-- Loop Only --" option to the HL7 segment ID dropdown
  useEffect(() => {
    if(FILETYPE === 'HL7'){
    if (hl7SegmentIdOptions?.isSuccess) {
      const segmentData = hl7SegmentIdOptions.data?.data || [];
      setSegmentOptions([t('SELECT'),t('LBL_LOOP_ONLY'), ...segmentData]);
    } else if (hl7SegmentIdOptions.isError) {
      console.error("Error fetching segment IDs:", hl7SegmentIdOptions.error);
    }
  }
  }, [hl7SegmentIdOptions]);

  const handleSourceNameSelect = (mapname) => {//?
  }
  const valuesHandler = (values) => {
    const { PARENT, RECORD_TYPE } = values;
    const updatedTranslatedData = eachDeep(cloneDeep(translatedData), (obj) => {
      if ( obj && obj.key === selectedNode.key) {
        // Update properties based on values
        obj.LOOP_SOURCE_NAME = RECORD_TYPE;
        obj.PARENT = PARENT;
        obj.TYPE = RECORD_TYPE;
        obj.ICON_DISPLAY = (["INSERT", "UPDATE", "DELETE"].includes(selectedNode?.STATEMENT) && FILETYPE === "DATABASE") ? 'QUERY' : (selectedNode?.key?.split("-")?.length <= 2 && FILETYPE === "JSON" && rightTree.currentTab === "body") ? "JSON_TOP_RECORD" :  'RECORD_LOOP';
        dispatch(
          treeActions.setRightTreeSelectedNode({
            screen: parentScreen,
            type: rightTree.currentTab,
            data: obj,
          })
        );
      }
  
      return obj;
    });
  
    dispatch(
      treeActions.setNewRightTreeTranslatedData({
        screen: parentScreen,
        type: rightTree.currentTab,
        data: updatedTranslatedData,
      })
    );
  };
  
  const recordLoopControlValuesHandler = (values) => {
     const {PARENT,MAPNAME,RECORD_TYPE,ACTION, INDEX, MAP_REPETITION_INDEX, SOURCE_REPEATING_FIELD, CUSTOM_DB_FIELD, PLUGIN_NAME, WF_ITEM_ID, WF_ITEM_LOCATION } = values
     const updatedTranslatedData = eachDeep(cloneDeep(translatedData), (obj) => {
      if ( obj && obj.key === selectedNode.key) {
        // Update properties based on values
        obj.LOOP_SOURCE_NAME = MAPNAME;
        obj.SOURCE_PARENT = PARENT;
        obj.SOURCE_TYPE = RECORD_TYPE;
        obj.ACTION= ACTION;
        obj.INDEX= INDEX;
        obj.MAP_REPETITION_INDEX = MAP_REPETITION_INDEX;
        obj.SOURCE_REPEATING_FIELD = SOURCE_REPEATING_FIELD;
        obj.CUSTOM_DB_FIELD = CUSTOM_DB_FIELD;
        obj.PLUGIN_NAME = PLUGIN_NAME;
        obj.WF_ITEM_ID = WF_ITEM_ID;
        obj.WF_ITEM_LOCATION = WF_ITEM_LOCATION;

         
        dispatch(
          treeActions.setRightTreeSelectedNode({
            screen: parentScreen,
            type: rightTree.currentTab,
            data: obj,
          })
        );
      }
  
      return obj;
    });
  
    dispatch(
      treeActions.setNewRightTreeTranslatedData({
        screen: parentScreen,
        type: rightTree.currentTab,
        data: updatedTranslatedData,
      })
    );

  }
  const COMPONENT_MAPPING_OBJ = {
    VARIABLE_COLLECTION: <VariableCollection />,
    CUSTOM_DB_FIELD: <CustomDBField />,
    RECORD_LOOP_CONTROL: (
      <SourceType
        node={{
          PARENT: selectedNode.SOURCE_PARENT,
          MAPNAME: selectedNode.LOOP_SOURCE_NAME,
          RECORD_TYPE: selectedNode.SOURCE_TYPE,
          key: selectedNode.key,
          ACTION: selectedNode?.ACTION,
					INDEX: selectedNode?.INDEX,
          MAP_REPETITION_INDEX: selectedNode?.MAP_REPETITION_INDEX,
          CUSTOM_DB_FIELD: selectedNode?.CUSTOM_DB_FIELD,
          PLUGIN_NAME: selectedNode?.PLUGIN_NAME
        }}
        valuesHandler={recordLoopControlValuesHandler}
        callerComponent='recordProperties'
      />
    ),
    INBOUND_DATA: (
      <SourceTypeInboundDataRenderer
        allowAllTreeSelection={true}
        treeData={treeData}
        expandActions={expandActions}
        setTab={setTab}
        handleSourceNameSelect={handleSourceNameSelect}
        valuesHandler={valuesHandler}
        src="INBOUND_DATA"
        node={{
          PARENT: selectedNode.PARENT,
          MAPNAME: selectedNode.LOOP_SOURCE_NAME,
          RECORD_TYPE: selectedNode.TYPE,
          key: selectedNode.key
        }}
        onlyRecord={true}
        isLoading={isLoading}
				showInboundTree={true}
      />
    ),
  };
 

  const handleLoopRecordChange = (e) => {
    const value = e.target.value;
    setLoopRecordValue(value);
    // setShowCheckbox(value!== 'RECORD_LOOP_CONTROL')
    setShowCheckboxBefore(value === "INBOUND_DATA");
  };

  const isInboundDependencyEDI = inboundDependencyTree?.templateHeader?.FILETYPE === "EDI";

  useEffect(() => {
    const baseOptions = LOOP_RECORD.filter((o) => o.value !== "RECORD_LOOP_CONTROL");
    const recordLoopControl = LOOP_RECORD.find((o) => o.value === "RECORD_LOOP_CONTROL");
    const options =
      isInboundDependencyEDI && recordLoopControl
        ? [...baseOptions, ...LOOP_RECORD_EDI_OPTIONS, recordLoopControl]
        : [...LOOP_RECORD];
    setLoopRecordOptions(options);
    const nodeType = selectedNode?.TYPE || selectedNode?.SOURCE_TYPE;
    const typeInOptions = nodeType && options.some((o) => o.value === nodeType);
    if (options[0].value === "INBOUND_DATA" && !inboundDependencyTree.templateFound) {
      setLoopRecordValue(t("SELECT"));
    } else if (typeInOptions) {
      setLoopRecordValue(nodeType);
    } else {
      setLoopRecordValue(options[0].value);
    }
  }, [inboundDependencyTree.templateFound, inboundDependencyTree?.templateHeader?.FILETYPE, isInboundDependencyEDI, selectedNode?.key, selectedNode?.TYPE, selectedNode?.SOURCE_TYPE]);

  useEffect(()=>{
    if(selectedNode && loopRecordOptions.length > 0){
     setDescription(selectedNode.DESCRIPTION || '');
     setHL7SegmentId(selectedNode?.FIELD?.VALUE || t('LBL_LOOP_ONLY'))
     loopRecordVal(selectedNode)
    }
  },[selectedNode?.key,loopRecordOptions])

  const loopRecordVal = (node) => {
     if (loopRecordOptions.some(option => option.value === node.TYPE)) {
      setLoopRecordValue(node.TYPE);
    } else {
      if (
        node?.SOURCE_TYPE === "INBOUND_DATA" &&
        !inboundDependencyTree.templateFound
      )
        setLoopRecordValue(t("SELECT"));
      else setLoopRecordValue(node?.SOURCE_TYPE || t('SELECT'));
    }
  }
  
  const getWorkflows = () => {
    const workflows = []
    inboundTemplateTree?.forEach((ele)=> {
      if(ele?.type === 'workflowGroup'){
        workflows.push(ele.title);
      }
    })
    return workflows;
  }

  const getTemplatesInWorkflowGroup = (workflow) => {
    const templates = [];
    inboundTemplateTree?.forEach((ele)=>{
      if(ele?.title === workflow){
        ele?.children?.forEach(t => (
          templates.push(t.title))
        );
        return;
      }
    })
    return templates;
  }
  
  useEffect(()=>{
    if (loopRecordValue === "INBOUND_DATA") {
      if(!DEPENDENCIES){
        setTreeData({});
        return;
      } 
      const dependency = DEPENDENCIES?.split("/") || [];      
      const workflow = dependency[0] || "";
      const template = dependency[2] || "";
      const workflows = getWorkflows();
      const templatesInWorkflow = getTemplatesInWorkflowGroup(workflow);

      if (
        workflows.includes(workflow) &&
        templatesInWorkflow.includes(template)
      ) {
        setIsLoading(true);
        dispatch(
          api.endpoints.getInboundTemplateData.initiate(
            {
              workflowGroupName: workflow,
              templateName: template,
              account: account,
            },
            { forceRefetch: true }
          )
        ).then((res) => {
          if(res?.error){
            setTreeData({})
            if (errorRef.current) {
              errorRef.current.textContent =
               res?.error.message || t("BOX_MSG_GENERAL_ERROR");
            }
            return;
          }
          const headerData = filterNodes(
            filterOutNodeTypes(inboundTemplateToRcTree(res.data, "HEADER"), []),
            "header",
          );
          const bodyData = filterNodes(
            filterOutNodeTypes(inboundTemplateToRcTree(res.data, "BODY"), []),
            "body",
          );
          const trailerData = filterNodes(
            filterOutNodeTypes(
              inboundTemplateToRcTree(res.data, "TRAILER"),
              []
            ),
            "trailer",
          );
          setTreeData((prev) => {
            return { header: headerData, body: bodyData, trailer: trailerData };
          });
        })
        .catch((error) => {
          if (errorRef.current) {
            errorRef.current.textContent = error.message || t("BOX_MSG_GENERAL_ERROR");
          }
        })
        .finally(() => {
          setIsLoading(false)
        })
      } 
    }
    setShowCheckbox(loopRecordValue !== 'RECORD_LOOP_CONTROL')

  },[DEPENDENCIES,loopRecordValue])

  const loopRecordMenuItems = useMemo(
    () =>
      loopRecordOptions.map((obj, key) => (
        <MenuItem key={key} value={obj.value}>
          {t(obj.title)}
        </MenuItem>
      )),
    [loopRecordOptions]
  );

  const submitChange = (key, value, callback) => {
  
   const updatedData = eachDeep(cloneDeep(translatedData),(obj) => {
      if (obj?.key === selectedNode.key) {
        if(key === 'DESCRIPTION'){
          obj.DESCRIPTION = value;
          if(!obj.TABLE_NAME || !obj.CONNECTION) obj.title = `${t("LBL_RECORD")} ${value}`
        }
        else if(key === 'TYPE'){
          if(value === t('SELECT')){
            delete obj.SOURCE_TYPE
            delete obj.LOOP_SOURCE_NAME
            delete obj.TYPE
            obj.ICON_DISPLAY = (FILETYPE === 'DATABASE' && ["INSERT", "UPDATE", "DELETE"].includes(obj?.STATEMENT)) ? 'QUERY' : (selectedNode?.key?.split("-")?.length <=2 && FILETYPE === "JSON" && rightTree.currentTab === "body") ? "JSON_TOP_RECORD" :  'RECORD';
            if(!obj.DESCRIPTION) obj.title = `${t("LBL_RECORD")}`
          } else if (value === "INBOUND_DATA") {
            obj.SOURCE_TYPE = value;
            delete obj.TYPE;
          } else {
            delete obj.SOURCE_TYPE
            delete obj.PARENT
            obj.ICON_DISPLAY = (FILETYPE === 'DATABASE' && ["INSERT", "UPDATE", "DELETE"].includes(obj?.STATEMENT)) ? 'QUERY' : (selectedNode?.key?.split("-")?.length <=2 && FILETYPE === "JSON" && rightTree.currentTab === "body") ? "JSON_TOP_RECORD" :  'RECORD_LOOP';
            obj.TYPE = value;
            obj.LOOP_SOURCE_NAME = value;
            obj.SOURCE_TYPE = value
            if(!obj.DESCRIPTION) obj.title = `${t("LBL_RECORD")} ${value}`
          }
          obj.LOOP = value === t("SELECT") ? "SINGLE" : "MULTI"
        } 
        else if(key === 'VALUE'){
          obj.FIELD.VALUE = value;
        } else {
          obj[key] = value;
        }
        dispatch(
          treeActions.setRightTreeSelectedNode({
            screen: parentScreen,
            type: rightTree.currentTab,
            data: obj,
          })
        );
      }
    });
  
    dispatch(
      treeActions.setNewRightTreeTranslatedData({
        screen: parentScreen,
        type: rightTree.currentTab,
        data: updatedData,
      })
    );
  
    if (callback && typeof callback === "function") {
      callback();
    }
  };

  const debounceChangeHandler = useCallback(debounce(submitChange, 500), [
    rightTree.currentTab,
    rightTree.tree,
  ]);

  return (
    <Stack spacing={"15px"} className="tp-segment">
      <FormControl variant="outlined" className="width-100" size="small">
        <TextField
          size="small"
          label={t("DESCRIPTION")}
          autoComplete="off"
          value={description}
          onChange={(e) => {
            setDescription(e.target.value);
            debounceChangeHandler("DESCRIPTION", e.target.value);
          }}
        />
      </FormControl>
      {FILETYPE === 'HL7' && (
          <FormControl className="width-100" size="small" variant="outlined">
          <Autocomplete
            freeSolo
            size="small"
            options={segmentOptions}
            value={hl7SegmentId || ''}
            onChange={(e,newValue) => {
              setHL7SegmentId(newValue);
              debounceChangeHandler("VALUE", newValue);
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label={t("LBL_HL7_SEGMENT_ID")}
                value={hl7SegmentId || ''}
                onChange={(e) => {
                  setHL7SegmentId(e.target.value);
                  debounceChangeHandler("VALUE", e.target.value);
                }}
             />
            )}
          />
        </FormControl>
      )}
      <FormControl variant="outlined" className="width-100" size="small">
        <InputLabel id="loopRecordDropdownLabel">
          {t("LBL_LOOP_REC_OPTIONAL")}
        </InputLabel>
        <Select
          className="width-100"
          MenuProps={{
            PaperProps: {
              sx: {
                maxHeight: 300,
              },
            },
          }}
          name="loopRecord"
          value={loopRecordValue}
          label={t("LBL_LOOP_REC_OPTIONAL")}
          onChange={(e) => {
            const newValue = e.target.value;
            const currentType = selectedNode?.TYPE || selectedNode?.SOURCE_TYPE;
            if (newValue === currentType) return;
            debounceChangeHandler("TYPE", newValue, () => {
              handleLoopRecordChange(e);
            });
          }}>
          {loopRecordMenuItems}
        </Select>
      </FormControl>
      { FILETYPE ==='EDI' && <EDIRecordProperties /> }
      { FILETYPE === 'JSON' && <JSONRecordProperties />}
      
      {(showCheckbox && showCheckboxBefore && FILETYPE !== 'DATABASE') && <RecordPropertiesCheckBox />}
      
      {loopRecordValue && !['INBOUND_DATA','RECORD_LOOP_CONTROL'].includes(loopRecordValue)  && COMPONENT_MAPPING_OBJ[loopRecordValue]}
      
      {loopRecordValue === "RECORD_LOOP_CONTROL" && FILETYPE === 'DATABASE'  && (
        <Typography sx={{ borderBottom: "solid 1px rgba(0,0,0,0.12)" }}>
          {t("LBL_DATA_SOURCE")}
        </Typography>
      )}
      
      {loopRecordValue === "INBOUND_DATA" && FILETYPE === 'DATABASE' && (
        <div className="flex" style={{ justifyContent: "space-between" }}>
          <Typography style={{ fontWeight: "bold" }}>
            {t("LBL_SHOWING_LOOPS_ONLY")}
          </Typography>
        </div>
      )}

      {loopRecordValue && ['INBOUND_DATA','RECORD_LOOP_CONTROL'].includes(loopRecordValue) && FILETYPE === 'DATABASE'  && COMPONENT_MAPPING_OBJ[loopRecordValue]}
      
      { FILETYPE === 'DATABASE' && <DatabaseRecordProperties />}
      
      {loopRecordValue === "RECORD_LOOP_CONTROL" && FILETYPE !== 'DATABASE'  && (
        <Typography sx={{ borderBottom: "solid 1px rgba(0,0,0,0.12)" }}>
          {t("LBL_DATA_SOURCE")}
        </Typography>
      )}
      
      {loopRecordValue === "INBOUND_DATA" && FILETYPE !== 'DATABASE' && (
        <div className="flex" style={{ justifyContent: "space-between" }}>
          <Typography style={{ fontWeight: "bold" }}>
            {t("LBL_SHOWING_LOOPS_ONLY")}
          </Typography>
        </div>
      )}
      
      {loopRecordValue && ['INBOUND_DATA','RECORD_LOOP_CONTROL'].includes(loopRecordValue) && FILETYPE !== 'DATABASE'  && COMPONENT_MAPPING_OBJ[loopRecordValue]}
       <p ref={errorRef} className="error"></p>
      
      {(showCheckbox && !showCheckboxBefore && FILETYPE !== 'DATABASE') && <RecordPropertiesCheckBox />}
    </Stack>
  );
}

export default OutboundRecordProperties;

export const filterNodes = (data) => {
  const newData = cloneDeep(data || []);

  const filterLoopItems = (data) => {
    return data
      .map((obj) => {
        if (
          obj.ICON_DISPLAY === "RECORD" ||
          obj.ICON_DISPLAY === "FIELD_LOOP" ||
          obj.ICON_DISPLAY === "TEMPLATE_BODY_DOTS" ||
          obj.ICON_DISPLAY === 'REPEATING_FIELD_GROUP' ||
          obj.ICON_DISPLAY ===  'QUERY' ||
          obj.ICON_DISPLAY === 'RECORD_TOP' ||
          (obj.ICON_DISPLAY === "FIELD_GROUP" &&
            (obj.children || []).some(
              (child) => (child.ICON_DISPLAY === "FIELD_GROUP" || child.ICON_DISPLAY === "REPEATING_FIELD_GROUP")
            )) ||
            (obj.ICON_DISPLAY === "ELEMENT" &&
             (obj.children.length > 0))
        ) {
          const { children,isLeaf, ...rest } = obj;
          const filteredChildren = children
            ? filterLoopItems(children)
            : undefined;

          // Include "children" property only if there are matching children
          return {
            ...rest,
            ...(filteredChildren && filteredChildren.length > 0 && { children: filteredChildren }),
          };
        }
        return null;
      })
      .filter(Boolean);
  };

  const records = filterLoopItems(newData);
  return records;
};



